/**
 * GroupTabs — Posts / My Groups / All Groups switcher plus the Create Group
 * action, shown under the main feed tabs when the Groups feed is active.
 */
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { GROUP_TABS } from '@wlb/shared/constants';
import type { GroupTabKey } from '@wlb/shared/constants';
import { PlusCircleIcon } from '../../components/ui/icons';
import './groups.css';

export type { GroupTabKey };

/** Shared between the tab buttons and the panel they control. */
export const GROUPS_PANEL_ID = 'groups-tabpanel';
export const groupTabId = (tab: GroupTabKey) => `groups-tab-${tab}`;

const TAB_CONFIG: { key: GroupTabKey; labelKey: string }[] = [
  { key: GROUP_TABS.POSTS, labelKey: 'home.groupTabs.posts' },
  { key: GROUP_TABS.MY_GROUPS, labelKey: 'home.groupTabs.myGroups' },
  { key: GROUP_TABS.ALL_GROUPS, labelKey: 'home.groupTabs.allGroups' },
];

interface GroupTabsProps {
  activeTab: GroupTabKey;
  onTabChange: (tab: GroupTabKey) => void;
  onCreateGroup: () => void;
}

export function GroupTabs({ activeTab, onTabChange, onCreateGroup }: GroupTabsProps) {
  const { t } = useTranslation();

  return (
    <div className="group-tabs">
      <div className="group-tabs__list" role="tablist" aria-label={t('home.topTabs.groups')}>
        {TAB_CONFIG.map(({ key, labelKey }) => {
          const isActive = activeTab === key;
          return (
            <button
              key={key}
              id={groupTabId(key)}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={GROUPS_PANEL_ID}
              tabIndex={isActive ? 0 : -1}
              className={`group-tabs__tab${isActive ? ' group-tabs__tab--active' : ''}`}
              onClick={() => onTabChange(key)}
            >
              {t(labelKey)}
            </button>
          );
        })}
      </div>
      <button type="button" className="group-tabs__create" onClick={onCreateGroup}>
        <PlusCircleIcon size={20} />
        {t('home.groupTabs.createGroup')}
      </button>
    </div>
  );
}
